import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import LockResetIcon from '@mui/icons-material/LockReset';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { AxiosError } from 'axios';
import { api } from '../../../../shared/services/api';
import { maskCNPJ } from '../../../../shared/utils/mask';

interface ForgotPasswordProps {
  open: boolean;
  handleClose: () => void;
}

const RecoveryCard = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: 460,
  margin: '0 auto',
  overflow: 'hidden',
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(3, 3, 2),
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
}));

export default function ForgotPassword({ open, handleClose }: ForgotPasswordProps) {
  const theme = useTheme();

  const [cnpj, setCnpj] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [cnpjError, setCnpjError] = React.useState('');
  const [emailError, setEmailError] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState(false);

  const resetForm = () => {
    setCnpj('');
    setEmail('');
    setCnpjError('');
    setEmailError('');
  };

  const handleCloseDialog = () => {
    resetForm();
    handleClose();
  };

  const handleCnpjChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    setCnpj(maskCNPJ(digits));
    if (cnpjError) setCnpjError('');
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailError) setEmailError('');
  };

  const validate = (): boolean => {
    let valid = true;

    const cnpjDigits = cnpj.replace(/\D/g, '');
    if (!cnpjDigits || cnpjDigits.length !== 14) {
      setCnpjError('CNPJ deve conter 14 dígitos.');
      valid = false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setEmailError('Insira um e-mail válido.');
      valid = false;
    }

    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setIsLoading(true);
      await api.post('/users/password-recovery', {
        cnpj: cnpj.replace(/\D/g, ''),
        email,
      });
    } catch (err: unknown) {
      // Silencioso por segurança — sempre mostra mensagem genérica
    } finally {
      setIsLoading(false);
      setSnackbar(true);
      resetForm();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            backgroundImage: 'none',
            borderRadius: 2,
            overflow: 'hidden',
          },
        }}
        maxWidth="xs"
        fullWidth
      >
        <RecoveryCard elevation={0}>
          {/* Header com ícone e título */}
          <CardHeader>
            <IconButton
              onClick={handleCloseDialog}
              sx={{ position: 'absolute', right: 8, top: 8, color: 'rgba(255,255,255,0.7)' }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
            <LockResetIcon sx={{ fontSize: 40 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Recuperar Senha
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, textAlign: 'center' }}>
              Sistema de Transmisão AJUD — Fidúcia SCM
            </Typography>
          </CardHeader>

          {/* Conteúdo */}
          <DialogContent sx={{ p: 3 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mb: 2.5,
                color: 'text.secondary',
                lineHeight: 1.5,
              }}
            >
              Informe o CNPJ da instituição responsável e o e-mail do operador.
              As instruções de recuperação serão enviadas para o e-mail cadastrado
              no sistema Sisbacen.
            </Typography>

            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
            >
              <TextField
                label="CNPJ da Instituição"
                placeholder="00.000.000/0000-00"
                value={cnpj}
                onChange={(e) => handleCnpjChange(e.target.value)}
                error={!!cnpjError}
                helperText={cnpjError || 'CNPJ conforme layout SISBAJUD v1.11'}
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  },
                }}
                inputProps={{ maxLength: 18 }}
              />

              <TextField
                label="E-mail do Operador"
                placeholder="operador@instituicao.com.br"
                type="email"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                error={!!emailError}
                helperText={emailError}
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  },
                }}
              />

              <LoadingButton
                type="submit"
                variant="contained"
                fullWidth
                loading={isLoading}
                sx={{
                  mt: 1,
                  py: 1.2,
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  fontSize: '0.95rem',
                }}
              >
                Enviar Instruções
              </LoadingButton>

              <Button
                variant="text"
                startIcon={<ArrowBackIcon />}
                onClick={handleCloseDialog}
                sx={{
                  textTransform: 'none',
                  color: theme.palette.primary.main,
                }}
              >
                Voltar ao Login
              </Button>
            </Box>
          </DialogContent>
        </RecoveryCard>
      </Dialog>

      {/* Snackbar de feedback — mensagem genérica por segurança */}
      <Snackbar
        open={snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Se o usuário existir, as instruções foram enviadas para o e-mail cadastrado.
        </Alert>
      </Snackbar>
    </>
  );
}
